import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, ArrowRight, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const CustomerLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/customer/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      toast.success(`Welcome back, ${data.name}!`);
      
      // Store customer info in sessionStorage
      sessionStorage.setItem('customer', JSON.stringify({
        id: data.customer_id,
        name: data.name,
        email: data.email,
        loyalty_tier: data.loyalty_tier
      }));

      navigate('/customer-portal');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4 pt-24">
      <div className="w-full max-w-md">
        {/* Back to Home */}
        <button
          onClick={() => navigate('/')}
          className="text-gray-400 hover:text-white mb-6 flex items-center space-x-2 transition-colors"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          <span>Back to Home</span>
        </button>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-white">Customer Portal</CardTitle>
            <CardDescription className="text-gray-400">
              Access your booking history, loyalty rewards, and more
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-200 flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>Email Address</span>
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-200 flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>Last 4 Digits of Phone</span>
                </label>
                <Input
                  type="text"
                  placeholder="1234"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  maxLength="4"
                  pattern="[0-9]{4}"
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
                <p className="text-xs text-gray-500">
                  Enter the last 4 digits of your phone number for verification
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                {loading ? 'Logging in...' : 'Access Portal'}
              </Button>

              <div className="pt-4 border-t border-zinc-800">
                <div className="flex items-start space-x-2 text-xs text-gray-400">
                  <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>
                    Your account is automatically created when you make your first booking.
                    No passwords required - we verify your identity using your email and phone number.
                  </p>
                </div>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-400">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/booking')}
                    className="text-red-500 hover:text-red-400 font-semibold"
                  >
                    Book your first detail
                  </button>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerLogin;
